const TIMEZONE = 'Europe/London';

/**
 * Parse a date string as UTC.
 * Database timestamps may lack a timezone suffix (e.g. "2026-04-10 13:06:40.7511341"),
 * which causes new Date() to treat them as browser-local time instead of UTC.
 * This function normalises the string to ISO 8601 with a Z suffix so it is always
 * interpreted as UTC before being converted to Europe/London for display.
 */
function parseAsUTC(dateString: string): Date {
  const trimmed = dateString.trim();
  // Already has timezone info — leave as-is
  if (/Z$/i.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    return new Date(trimmed);
  }
  // Replace space separator with T for ISO 8601, append Z for UTC
  return new Date(trimmed.replace(' ', 'T') + 'Z');
}

/**
 * Format a UTC timestamp as date only: "10 Apr 2026 (UK time)"
 */
export function formatDateUK(dateString: string | null): string {
  if (!dateString) return 'N/A';
  try {
    const formatted = new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: TIMEZONE,
    }).format(parseAsUTC(dateString));
    return `${formatted} (UK time)`;
  } catch {
    return 'Invalid date';
  }
}

/**
 * Format a UTC timestamp as date + time: "10 Apr 2026, 14:06 (UK time)"
 */
export function formatDateTimeUK(dateString: string | null): string {
  if (!dateString) return 'N/A';
  try {
    const formatted = new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: TIMEZONE,
    }).format(parseAsUTC(dateString));
    return `${formatted} (UK time)`;
  } catch {
    return 'Invalid date';
  }
}
